<template>
	<div>
		<h2 class="mt-8 text-2xl font-bold leading-9 tracking-tight text-gray-900 dark:text-white">
			Pay an Invoice
		</h2>

		<!-- shown in place of the form once a payment goes through, so the visitor is left with an
			answer rather than an empty form -->
		<div
			v-if="confirmation"
			class="mt-10 text-center">
			<CheckCircleIcon
				class="mx-auto h-12 w-12 text-green-500"
				aria-hidden="true" />

			<h3 class="mt-4 text-lg font-bold text-gray-900 dark:text-white">
				{{ confirmation.settled ? 'Payment Received' : 'Payment Submitted' }}
			</h3>

			<p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
				<template v-if="confirmation.settled">
					Thank you. Your payment of {{ currencyFormatter.format(confirmation.amount) }} for invoice
					#{{ confirmation.invoiceNumber }} has been received.
				</template>
				<template v-else>
					Thank you. Your payment of {{ currencyFormatter.format(confirmation.amount) }} for invoice
					#{{ confirmation.invoiceNumber }} is processing. ACH Bank payments can take up to 4 business days
					to process.
				</template>
			</p>

			<tw-button
				@click="payAnotherInvoice()"
				class="btn-db-yellow mt-8 w-full">
				Pay Another Invoice
			</tw-button>
		</div>

		<template v-else>
			<p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
				No account needed. Enter the account number and invoice number printed on your invoice.
			</p>

			<div class="mt-10">
				<InvoiceLookupForm
					:loading="loading"
					:message="message"
					@submit="findInvoice" />
			</div>
		</template>

		<v-divider-label
			label="Or"
			class-name="my-6" />

		<router-link
			:to="{name: 'SignIn'}"
			class="button btn-white w-full flex">
			Customer Login
		</router-link>

		<!-- opened by the page once a lookup succeeds, so the invoice and the payment share one dialog.
			keyed by the token, which every lookup mints afresh, so each lookup gets a new dialog rather
			than one still holding the last payment's state -->
		<PayInvoiceDialog
			v-if="invoice"
			:key="paymentToken"
			ref="payInvoiceDialog"
			:invoice="invoice"
			:payment-token="paymentToken"
			@paid="showConfirmation"
			@closed="clearInvoice()"
			@verification-canceled="clearPendingVerification(invoice)"
			@verification-started="markPendingVerification()"
			@verification-completed="showConfirmation" />

		<cloudflare-turnstile ref="turnstileRef" />
	</div>
</template>

<script lang="ts">
	export default { name: 'PayInvoiceMain' } // name the component
</script>

<script setup lang="ts">
	// api
	import lookupInvoice, {
		guestPayErrorMessage,
		InvoiceLookupPayload
	} from '@/utilities/guestPayApi'

	// components
	import InvoiceLookupForm from './components/InvoiceLookupForm.vue'
	import PayInvoiceDialog from './components/PayInvoiceDialog.vue'

	// icons
	import {
		CheckCircleIcon
	} from '@heroicons/vue/24/outline'

	// types
	import {
		Invoice
	} from '@/types'

	// utilities
	import {
		currencyFormatter
	} from '@/utilities/format'

	import {
		applyInvoiceDueStatus,
		clearPendingVerification
	} from '@/utilities/invoiceStatus'

	// vue
	import {
		nextTick,
		ref
	} from 'vue'

	// the status FileMaker sets from the payment_intent webhook once microdeposits are on their way
	const PENDING_VERIFICATION_STATUS = 'pending_verification'

	// what the dialog reports once a payment goes through. the amount is what Stripe actually took,
	// card fee included, and settled is false for a bank payment that is still clearing
	interface PaymentConfirmation {
		invoiceNumber: string
		amount: number
		settled: boolean
	}

	// the same message covers a wrong invoice number, a wrong account ID, and a mismatched pair, so
	// the page never confirms that one half of a guess was right
	const NOT_FOUND_MESSAGE = "We couldn't find that invoice. Please check the account and invoice numbers and try again."
	const ERROR_MESSAGE = "There was an issue looking up your invoice. Please try again or contact us for assistance."

	const invoice = ref<Invoice | null>(null)

	// kept after a successful lookup. the payment routes have no Turnstile of their own, so this
	// signed token is what authorizes them, and it carries the invoice and account it was issued for
	const paymentToken = ref("")

	const confirmation = ref<PaymentConfirmation | null>(null)
	const loading = ref(false)
	const message = ref("")

	const turnstileRef = ref<{ execute: () => Promise<string> } | null>(null)
	const payInvoiceDialog = ref<{ openDialog: () => Promise<void> } | null>(null)

	// looks up an invoice and shows it, falling back to the form with an explanation when it misses.
	// a filled honeypot is reported as not found rather than as a rejection
	async function findInvoice(submitted: Omit<InvoiceLookupPayload, "token">) {
		try {
			loading.value = true
			message.value = ""

			if (submitted.honeypot) {
				message.value = NOT_FOUND_MESSAGE
				return
			}

			const token = await turnstileRef.value?.execute() ?? ""

			const response = await lookupInvoice({
				invoiceNumber: submitted.invoiceNumber.trim(),
				accountNumber: submitted.accountNumber.trim(),
				honeypot: submitted.honeypot,
				token,
			})

			if (response.result === 'OK' && response.invoice) {
				invoice.value = withDueDate(response.invoice)
				paymentToken.value = response.paymentToken ?? ""

				// the dialog is only rendered once there is an invoice, so it has to exist before
				// it can be told to open
				await nextTick()
				await payInvoiceDialog.value?.openDialog()
				return
			}

			// the Lambda explains its own rejections, such as a failed security check, so its message
			// is preferred over the generic one wherever it sent one
			message.value = response.message
				|| (response.result === 'notFound' ? NOT_FOUND_MESSAGE : ERROR_MESSAGE)
		}
		catch (error: any) {
			// Amplify rejects on any non 2xx, so a rejection the Lambda explained arrives as a throw
			message.value = guestPayErrorMessage(error) || ERROR_MESSAGE
		}
		finally {
			loading.value = false
		}
	}

	// swaps the form for a confirmation once a payment goes through. the invoice is not looked up
	// again: FileMaker only hears from the webhook, so a fresh read would still show it unpaid, and
	// reopening the dialog would only start a second payment for the Lambda to refuse
	function showConfirmation(paid: PaymentConfirmation) {
		confirmation.value = paid

		// drops the invoice and token too, which unmounts the dialog
		clearInvoice()
	}

	// returns from the confirmation to an empty lookup form
	function payAnotherInvoice() {
		confirmation.value = null
	}

	// fills in the calculated due date and past due status on a copy of the invoice, using the same
	// rules as the signed in invoice list: past due on or after the due date
	function withDueDate(found: Invoice): Invoice {
		const dated = { ...found }

		applyInvoiceDueStatus(dated)

		return dated
	}

	// marks the invoice as awaiting a deposit verification straight away. the mirror of
	// clearPendingVerification: FileMaker only finds out from the webhook, and the card has to show
	// what the visitor just started rather than waiting for the next lookup
	function markPendingVerification() {
		if (!invoice.value) return

		invoice.value.paymentStatus = PENDING_VERIFICATION_STATUS
	}

	// returns to the lookup form so another invoice can be found
	function clearInvoice() {
		invoice.value = null
		paymentToken.value = ""
		message.value = ""
	}
</script>
